import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { useState, type ReactElement } from 'react';
import { Plus } from 'lucide-react';
import { Button, Typography } from 'antd';

import type { DealStage } from '../api/deals.client.js';
import type { KanbanColumnModel } from '../lib/kanban.js';
import { DealCard } from './DealCard.js';

const { Text } = Typography;

/** Spec D `--font-mono`: суммы и счётчики канбана — mono + tabular-nums. */
const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

export interface KanbanBoardProps {
  columns: KanbanColumnModel[];
  isUpdating: boolean;
  onCreateInStage: (stage: DealStage) => void;
  onMoveDeal: (dealId: string, toStage: DealStage) => void;
  onToggleSelected: (dealId: string) => void;
  selectedIds: ReadonlySet<string>;
}

export function KanbanBoard({
  columns,
  isUpdating,
  onCreateInStage,
  onMoveDeal,
  onToggleSelected,
  selectedIds,
}: KanbanBoardProps): ReactElement {
  useI18nSubscription();
  const [draggedDealId, setDraggedDealId] = useState<string | null>(null);

  return (
    <div
      className="crm-radius-l h-full w-full overflow-hidden"
      style={{
        border: '1px solid var(--ant-color-border-secondary)',
        background: 'var(--ant-color-bg-container)',
      }}
    >
      <div className="flex h-full overflow-x-auto" data-testid="deals-kanban-board">
        {columns.map((column, index) => (
          <section
            key={column.stage}
            data-testid={`kanban-column-${column.stage}`}
            className="flex h-full min-w-0 shrink-0 flex-col"
            style={{
              width: 280,
              ...(index > 0 ? { borderLeft: '1px solid var(--ant-color-border-secondary)' } : {}),
            }}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            onDrop={(event) => {
              event.preventDefault();
              if (draggedDealId !== null) {
                onMoveDeal(draggedDealId, column.stage);
              }
              setDraggedDealId(null);
            }}
          >
            {/* Spec D `.pipe-col-head`: uppercase micro-label, hairline bar,
                mono count — колонка не карточка, а секция одной поверхности. */}
            <header
              className="flex items-center gap-2"
              style={{
                padding: '9px 12px',
                borderBottom: '1px solid var(--ant-color-border-secondary)',
              }}
            >
              <h2
                className="m-0 truncate"
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: 'var(--ant-color-text-secondary)',
                }}
              >
                {getEnumLabel('dealStage', column.stage, i18n.t)}
              </h2>
              <span
                className="min-w-0 flex-1"
                style={{ height: 1, background: 'var(--ant-color-border-secondary)' }}
              />
              <span
                style={{
                  fontSize: 10.5,
                  color: 'var(--ant-color-text-tertiary)',
                  fontFamily: MONO_FONT,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {column.count}
              </span>
              <Button
                type="text"
                size="small"
                aria-label={i18n.t(
                  'crm.features.dynamic.features.deals.components.kanbanboard.58',
                  { p0: getEnumLabel('dealStage', column.stage, i18n.t) },
                )}
                icon={<Plus size={14} />}
                onClick={() => onCreateInStage(column.stage)}
                style={{
                  width: 24,
                  height: 24,
                  padding: 0,
                  color: 'var(--ant-color-text-secondary)',
                }}
              />
            </header>
            <div className="flex items-center gap-2" style={{ padding: '4px 12px 6px' }}>
              <Text
                data-testid={`kanban-column-${column.stage}-value`}
                style={{
                  fontSize: 11,
                  fontFamily: MONO_FONT,
                  fontVariantNumeric: 'tabular-nums',
                  color: 'var(--ant-color-text)',
                }}
              >
                {column.valueLabel}
              </Text>
              <Text
                data-testid={`kanban-column-${column.stage}-attention`}
                style={{
                  fontSize: 10,
                  color: 'var(--ant-color-text-tertiary)',
                  whiteSpace: 'nowrap',
                }}
              >
                {i18n.t('crm.features.features.deals.components.kanbanboard.без_шага')}{' '}
                {column.needsAttentionCount}
              </Text>
            </div>
            <div
              className="hide-scrollbar flex flex-1 flex-col overflow-y-auto"
              style={{ padding: '8px 8px 10px', gap: 6 }}
            >
              {column.cards.length > 0 ? (
                column.cards.map((card) => (
                  <DealCard
                    key={card.dealId}
                    card={card}
                    isSelected={selectedIds.has(card.dealId)}
                    isUpdating={isUpdating}
                    onMoveForward={onMoveDeal}
                    onToggleSelected={onToggleSelected}
                    onDragStart={setDraggedDealId}
                  />
                ))
              ) : (
                <div
                  className="crm-radius-s flex items-center justify-center"
                  style={{
                    border: '1px dashed var(--ant-color-border-secondary)',
                    padding: '18px 12px',
                  }}
                >
                  <Text style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
                    {i18n.t(
                      'crm.features.features.deals.components.kanbanboard.в_этой_стадии_пока_нет_сделок',
                    )}
                  </Text>
                </div>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
